import { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/perms";
import { fdate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { VisitReasonPicker, reasonsError, ReasonChips } from "@/components/pawkt/VisitReasons";
import { UserPlus, DoorOpen, Stethoscope, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const useRefresh = () => { const qc = useQueryClient(); return () => ["vet-encs", "vet-dash", "bookings"].forEach((k) => qc.invalidateQueries({ queryKey: [k] })); };
const isToday = (iso) => iso && new Date(iso).toDateString() === new Date().toDateString();
const stop = (fn) => (ev) => { ev.stopPropagation(); fn(); };

// Row actions: reception "Arrived" (today's scheduled) and vet "Start consultation" (waiting). Explicit, per pet.
export function VetRowActions({ e }) {
  const { user } = useAuth();
  const refresh = useRefresh();
  const [busy, setBusy] = useState(false);
  const run = async (fn, msg) => { setBusy(true); try { const r = await fn(); toast.success(msg, r?.data?.booking_sync?.reason ? { description: r.data.booking_sync.reason } : undefined); refresh(); } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); } };
  const canArrive = hasPerm(user, "bookings.edit") || hasPerm(user, "vet.view");
  if (e.status === "scheduled" && isToday(e.visit_date) && canArrive) {
    return <span role="button" tabIndex={0} aria-disabled={busy} onClick={stop(() => run(() => api.post(`/veterinary/encounters/${e.id}/arrive`), "Marked arrived · in waiting queue"))} data-testid={`vet-arrive-${e.id}`}
      className="inline-flex items-center gap-1 rounded-md border border-emerald-300 bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-100"><DoorOpen className="h-3 w-3" /> Arrived</span>;
  }
  if (e.status === "waiting" && hasPerm(user, "vet.edit")) {
    return <span role="button" tabIndex={0} onClick={stop(() => run(() => api.put(`/veterinary/encounters/${e.id}`, { status: "in_consultation" }), "Consultation started"))} data-testid={`vet-start-${e.id}`}
      className="inline-flex items-center gap-1 rounded-md bg-slate-900 px-2 py-1 text-xs font-semibold text-white hover:bg-slate-700"><Stethoscope className="h-3 w-3" /> Start consultation</span>;
  }
  return null;
}

export function WalkInButton() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [pet, setPet] = useState(null);
  const [reasons, setReasons] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const rq = useRef("");
  const refresh = useRefresh();
  const { data: pets = [] } = useQuery({ queryKey: ["walkin-pets", q], enabled: open && q.trim().length >= 2,
    queryFn: async () => (await api.get("/pets", { params: { search: q.trim(), limit: 10 } })).data.items || [] });
  const reset = () => { setQ(""); setPet(null); setReasons([]); setErr(""); rq.current = `walkin_${Date.now()}_${Math.random().toString(36).slice(2)}`; };
  const save = async () => {
    const e = !pet ? "Select a pet" : reasonsError(reasons);
    setErr(e); if (e) return;
    setBusy(true);
    try { await api.post("/veterinary/walk-in", { pet_id: pet.id, visit_reasons: reasons, request_id: rq.current }); toast.success("Walk-in added to waiting queue"); refresh(); setOpen(false); }
    catch (x) { toast.error(apiErr(x)); } finally { setBusy(false); }
  };
  return (
    <>
      <Button variant="outline" onClick={() => { reset(); setOpen(true); }} data-testid="vet-walkin-btn" className="gap-1.5"><UserPlus className="h-4 w-4" /> Add walk-in</Button>
      {open && (
        <Dialog open onOpenChange={setOpen}>
          <DialogContent className="bg-white max-w-md" data-testid="vet-walkin-dialog">
            <DialogHeader><DialogTitle className="font-display">Add walk-in</DialogTitle></DialogHeader>
            {pet ? (
              <div className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2 text-sm" data-testid="vet-walkin-pet-selected"><span>{pet.name}</span><button onClick={() => setPet(null)} className="text-xs text-slate-500">Change</button></div>
            ) : (
              <div>
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search pet name (min 2 letters)" data-testid="vet-walkin-pet-search" className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm" />
                <div className="mt-1 max-h-40 overflow-y-auto">
                  {pets.map((p) => <button key={p.id} onClick={() => setPet(p)} data-testid={`vet-walkin-pet-${p.id}`} className="block w-full rounded px-2 py-1.5 text-left text-sm hover:bg-slate-50">{p.name} <span className="text-xs text-slate-400">{p.species || ""}</span></button>)}
                </div>
              </div>
            )}
            <VisitReasonPicker value={reasons} onChange={setReasons} />
            {err && <p className="text-xs text-rose-600" data-testid="vet-walkin-error">{err}</p>}
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} disabled={busy} data-testid="vet-walkin-save" className="bg-slate-900 text-white">{busy ? "Adding…" : "Add to queue"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

export function PriorConsults({ petId, currentId }) {
  const [open, setOpen] = useState(false);
  const { data } = useQuery({ queryKey: ["vet-encs", "pet", petId], enabled: open,
    queryFn: async () => (await api.get("/veterinary/encounters", { params: { pet_id: petId, limit: 50 } })).data.items });
  const rows = (data || []).filter((x) => x.id !== currentId);
  return (
    <div className="rounded-lg border border-slate-200" data-testid="vet-prior-consults">
      <button onClick={() => setOpen(!open)} className="flex w-full items-center justify-between px-3 py-2 text-sm font-semibold text-slate-700" data-testid="vet-prior-toggle">
        Previous consultations <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="divide-y divide-slate-100 px-3 pb-2 text-xs">
          {!data ? <p className="py-2 text-slate-400">Loading…</p> : rows.length === 0 ? <p className="py-2 text-slate-400" data-testid="vet-prior-empty">No previous consultations.</p> : rows.map((x) => (
            <div key={x.id} className="py-2" data-testid={`vet-prior-${x.id}`}>
              <div className="flex justify-between"><span className="font-medium capitalize text-slate-700">{(x.status || "").replace("_", " ")}</span><span className="text-slate-400">{fdate(x.visit_date || x.created_at)}</span></div>
              <ReasonChips reasons={x.visit_reasons} />
              {x.chief_complaint && <p className="mt-0.5 text-slate-500">{x.chief_complaint}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
